import { useState } from "react";
import { userFacingMessage } from "@veto/domain";
import { wipeAllLocalData } from "@veto/storage";
import { clearWrappedVault } from "../nativeVault";

/** Same confirmation word as the phone app ("Type DELETE to confirm"). */
const CONFIRM_WORD = "DELETE";

/**
 * Local secure reset — wipes IndexedDB + veto.* web storage, then reloads to onboarding.
 * Relays / remote ciphertext are untouched (operator reset remains forbidden).
 */
export function SecureWipeButton({ onToast }: { onToast: (msg: string) => void }) {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const ready = confirm.trim().toUpperCase() === CONFIRM_WORD;

  return (
    <div className="stack danger-zone" role="region" aria-label="Delete everything on this device">
      <h4>Delete everything on this device</h4>
      <p className="muted text-sm">
        <strong className="text-danger">
          This permanently erases your identity, contacts and messages from this browser. It cannot
          be undone.
        </strong>{" "}
        Your contacts will need your new invite link if you start again. Relays keep the sealed
        envelopes they already hold — this is not a remote wipe — and an encrypted backup you
        exported is the only way back.
      </p>
      <div className="field">
        <label htmlFor="wipeConfirm">
          Type <strong>{CONFIRM_WORD}</strong> to confirm
        </label>
        <input
          id="wipeConfirm"
          type="text"
          autoComplete="off"
          spellCheck={false}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder={CONFIRM_WORD}
          aria-describedby="wipeConfirmHint"
        />
      </div>
      <p id="wipeConfirmHint" className="muted text-xs" style={{ margin: 0 }}>
        {ready
          ? "Confirmed — this erases everything here and starts Veto from the beginning."
          : `The button stays off until you type ${CONFIRM_WORD}.`}
      </p>
      <button
        className="btn danger"
        type="button"
        disabled={busy || !ready}
        aria-disabled={busy || !ready}
        title={ready ? "Delete everything on this device" : `Type ${CONFIRM_WORD} to confirm`}
        onClick={() => {
          if (!ready || busy) return;
          setBusy(true);
          void (async () => {
            try {
              await wipeAllLocalData();
              // SEC-STOR: purge the wrapped vault from the SecureVault backend
              // (Preferences/Keychain/Keystore) — it does not live in IndexedDB.
              await clearWrappedVault();
              for (const key of Object.keys(localStorage)) {
                if (key.startsWith("veto.")) localStorage.removeItem(key);
              }
              for (const key of Object.keys(sessionStorage)) {
                if (key.startsWith("veto.")) sessionStorage.removeItem(key);
              }
              onToast("Everything on this device was deleted — restarting");
              window.location.assign("/");
            } catch (e) {
              onToast(userFacingMessage(e));
              setBusy(false);
            }
          })();
        }}
      >
        {busy ? "Deleting…" : "Delete everything"}
      </button>
    </div>
  );
}
